import { syncStore$ } from "@/stores/sync.v2.store"

/**
 * Utility functions for converting between syncStore powerDialer values and listId strings
 * Used throughout the PowerDialer components for consistent state management
 */

/**
 * Convert syncStore powerDialer values to listId string for backwards compatibility
 * @param powerDialerTargetType - The target type from syncStore
 * @param powerDialerTargetId - The target ID from syncStore
 * @returns listId string or null if no valid selection
 */
export function syncToPowerDialerListId(powerDialerTargetType: string | null | undefined, powerDialerTargetId: number | null | undefined): string | null {
  if (powerDialerTargetId && powerDialerTargetId > 0) {
    return powerDialerTargetId.toString()
  }

  if (!powerDialerTargetType) return null

  if (powerDialerTargetType === "personal") {
    return "all-lists"
  }

  if (powerDialerTargetType === "sequence") {
    return "all-sequences"
  }

  return null
}

/**
 * Convert listId string back to syncStore powerDialer values
 * @param listId - The listId string (from URL query or component state)
 * @returns Object with powerDialerTargetType and powerDialerTargetId
 */
export function listIdToSyncValues(listId: string | null): {
  powerDialerTargetType: "personal" | "team" | "sequence" | null
  powerDialerTargetId: number | null
} {
  if (!listId) {
    return {
      powerDialerTargetType: null,
      powerDialerTargetId: null,
    }
  }

  if (listId === "all-lists") {
    return {
      powerDialerTargetType: "team",
      powerDialerTargetId: null,
    }
  }

  if (listId === "all-sequences") {
    return {
      powerDialerTargetType: "sequence",
      powerDialerTargetId: null,
    }
  }

  // Try to parse as numeric ID
  const numericId = parseInt(listId)
  if (isNaN(numericId) || numericId <= 0) {
    return {
      powerDialerTargetType: null,
      powerDialerTargetId: null,
    }
  }

  // Default to personal list for numeric IDs
  // Numeric list IDs are non-sequence selections by default.
  return {
    powerDialerTargetType: "team",
    powerDialerTargetId: numericId,
  }
}

/**
 * Check if there's a valid PowerDialer selection
 * @param powerDialerTargetType - The target type from syncStore
 * @param powerDialerTargetId - The target ID from syncStore
 * @returns true if there's a valid selection
 */
export function hasPowerDialerSelection(powerDialerTargetType: string | null | undefined, powerDialerTargetId: number | null | undefined): boolean {
  if (powerDialerTargetId && powerDialerTargetId > 0) return true
  if (!powerDialerTargetType) return false

  // Valid selections:
  // - personal with or without ID (all-lists or specific list)
  // - sequence with or without ID (all-sequences or specific sequence)
  return powerDialerTargetType === "personal" || powerDialerTargetType === "sequence"
}
